import { NAVBAR_HEIGHT } from '#app/components/AppBar/constants.js';
import { Text } from '#app/components/Primitives/Text.js';
import { ProjectBrandYear } from './ProjectLayout.styles.js';
import { css, styled } from 'styled-components';

const Author = styled.div`
  margin-bottom: 24px;
`;

const Container = styled.div`
  position: relative;
`;

const Card = styled.div`
  ${({ theme }) => css`
    border-radius: 20px;
    background-color: ${theme.colorsV2.white};
    padding: 32px 0;

    ${theme.mediaQueries.lg} {
      border: 1px solid ${theme.colorsV2.gray30};
      padding: 54px 0;
      width: 100%;
      min-width: 640px;
      position: relative;
    }

    @media screen and (min-width: 1441px) {
      border-radius: 20px;
    }
  `}
`;

const Content = styled.div`
  ${({ theme }) => css`
    margin: 0 auto;
    box-sizing: border-box;
    width: calc(100vw - 32px);

    ${theme.mediaQueries.lg} {
      width: calc(100% - 64px);
    }
  `}
`;

const Details = styled.div<{ addTopPadding: boolean }>`
  ${({ addTopPadding, theme }) => css`
    border-bottom: 1px solid ${theme.colorsV2.gray30};

    ${addTopPadding &&
    css`
      padding-top: 16px;
    `}

    > * {
      margin-bottom: 16px;
    }

    ${theme.mediaQueries.lg} {
      > * {
        margin-bottom: 24px;
      }
    }
  `}
`;

const EditButton = styled.div`
  ${({ theme }) => css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: ${theme.colorsV2.white};
    border-radius: 50px;
    border: 1px solid ${theme.colorsV2.gray30};
  `}
`;

const EditButtonMobile = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: ${theme.colorsV2.white};
    border-radius: 50px;
  `}
`;

const Title = styled(Text).attrs({
  as: 'h1',
  textStyle: { _: 'fifthLevelHeadline', lg: 'thirdLevelHeadline' },
})``;

const Participants = styled.div`
  display: flex;
  flex-direction: column;
`;

const OwnerDetails = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 16px;
`;

const OwnerName = styled(Text).attrs({ textStyle: 'subtitleRegular' })``;

const OwnerTitle = styled(Text).attrs({ textStyle: 'captionRegular' })`
  ${({ theme }) => css`
    font-weight: 400;
    color: ${theme.colorsV2.gray60};
  `}
`;

const MinimizedContainer = styled.div<{ hasCollaborators: boolean }>`
  ${({ hasCollaborators = false, theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;

    ${theme.mediaQueries.lg} {
      justify-content: space-between;
      ${hasCollaborators &&
      css`
        min-height: 88px;
      `};
    }
  `}
`;

const ExpandedContainer = styled.div`
  ${({ theme }) => css`
    margin-top: 16px;
    width: 100%;
    max-width: 576px;

    ${theme.mediaQueries.lg} {
      margin-top: unset;
    }

    ${theme.mediaQueries.xl} {
      max-width: unset;
    }
  `}
`;

const CollapsedRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-height: 88px;
`;

const Editor = styled.div`
  ${() => css`
    margin-top: 24px;
  `}
`;

const ThematicBreak = styled.div`
  ${({ theme }) => css`
    margin: 40px 0;
    border-bottom: 1px solid ${theme.colorsV2.gray30};
  `}
`;

const Year = styled(Text).attrs({
  as: 'p',
  textStyle: 'subtitleRegular',
})`
  text-align: center;
  font-weight: 600;
`;

const PartnerWithAction = styled.div`
  ${({ theme }) => css`
    position: relative;
    margin-top: 0px;

    ${theme.mediaQueries.lg} {
      margin-top: -32px;
    }
  `}
`;
const MobileActions = styled.div`
  ${({ theme }) => css`
    position: fixed;
    bottom: 26px;
    right: 20px;
    display: flex;
    gap: 16px;
    z-index: ${theme.zIndices.toolbar};
    button,
    > div:first-child {
      border-radius: 50px;
      box-shadow: ${theme.boxShadow.pageActions};
    }
  `}
`;

const ToothbrushContainer = styled.div`
  ${({ theme }) => css`
    display: none;
    position: fixed;
    gap: 16px;
    right: 16px;
    z-index: ${theme.zIndices.menu};
    top: ${NAVBAR_HEIGHT + 48}px;

    ${theme.mediaQueries.lg} {
      display: flex;
      flex-direction: column;
      right: 52px;
    }

    ${theme.mediaQueries.xl} {
      width: 80px;
      position: fixed;
      bottom: unset;
    }

    @media screen and (min-width: 1500px) {
      right: 64px;
    }

    @media screen and (min-width: 1600px) {
      right: 88px;
    }

    button,
    ${EditButton} {
      box-shadow: ${theme.boxShadowV2.basic20};
      border: 1px solid ${theme.colorsV2.gray40};

      svg {
        color: ${theme.colorsV2.gray70};
      }
    }
  `}
`;

export const ProjectLayout = Object.assign({}, Container, {
  Author,
  BrandYear: ProjectBrandYear,
  Card,
  CollapsedRow,
  Content,
  Details,
  EditButton,
  EditButtonMobile,
  Editor,
  ExpandedContainer,
  MinimizedContainer,
  MobileActions,
  OwnerDetails,
  OwnerName,
  OwnerTitle,
  Participants,
  PartnerWithAction,
  ThematicBreak,
  Title,
  ToothbrushContainer,
  Year,
});
